/**
 * Internal chain-config plumbing. Maps the `Network` selector type to the
 * known public / private chains. Not part of the public SDK surface — the
 * consumer-facing API is `Network` + `ChainOpts`. This file is consumed
 * internally by the chain resolver and the HTTP client.
 *
 * BRIDs come from the Rust core via NAPI so all language bindings agree
 * byte-for-byte; node URLs are wire constants that live alongside them.
 */
import {
  DEFAULT_BLOCKCHAIN_RID,
  DEFAULT_CHROMIA_NODE_URLS,
} from "./constants.js";
import { native } from "./native.js";
import type { Network } from "./types.js";

const DEFAULT_PRIVATE_NODE_URLS: readonly string[] = Object.freeze(
  native.defaultPrivateNodeUrls(),
);

const DEFAULT_PRIVATE_BLOCKCHAIN_RID: string =
  native.DEFAULT_PRIVATE_BLOCKCHAIN_RID;

export interface ChainConfig {
  readonly network: Network;
  readonly blockchainRid: string;
  readonly nodeUrls: readonly string[];
}

export const PUBLIC_CHAIN: ChainConfig = {
  network: "public",
  blockchainRid: DEFAULT_BLOCKCHAIN_RID,
  nodeUrls: DEFAULT_CHROMIA_NODE_URLS,
};

export const PRIVATE_CHAIN: ChainConfig = {
  network: "private",
  blockchainRid: DEFAULT_PRIVATE_BLOCKCHAIN_RID,
  nodeUrls: DEFAULT_PRIVATE_NODE_URLS,
};

export function chainForNetwork(network: Network): ChainConfig {
  return network === "private" ? PRIVATE_CHAIN : PUBLIC_CHAIN;
}
